<template>
  <div class="fill-height">
    <v-card flat>
      <v-card-title class="py-0 px-0">
        <toolbar-period-selector
          v-model="dataTable.filter.filter_date"
          default-period="today"
          hide-year
        >
          <v-btn
            v-if="userCanManage && (userIsOwner || userIsSuperAdmin)"
            :color="$t('Customer.Table.Button.Export.Color')"
            :disabled="!dataTable.filter.filter_date"
            :icon="$vuetify.breakpoint.xsOnly"
            class="elevation-0 ma-1"
            text
            @click="onExportButtonClick"
          >
            <v-icon :left="$vuetify.breakpoint.smAndUp">
              {{ $t('Customer.Table.Button.Export.Icon') }}
            </v-icon>&nbsp;
            <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Customer.Table.Button.Export.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>

          <v-spacer />

          <filter-search v-model="dataTable.search.terms" />

          <filter-options
            v-model="filtersModel"
            :filters="filterData"
            max-width="450"
          />
        </toolbar-period-selector>
      </v-card-title>

      <v-divider />

      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col cols="12">
            <v-data-table
              :footer-props="dataTable.footerProps"
              :headers="dataTableHeaders"
              :height="dataTable.resize.bodyHeight"
              :items="dataTable.data"
              :loading="dataTable.loading"
              :loading-text="dataTable.text.loadingText"
              :no-data-text="dataTable.text.noDataText"
              :no-results-text="dataTable.text.noResultsText"
              :options.sync="dataTable.options"
              :search="dataTable.search.terms"
              :server-items-length="dataTable.total"
              fixed-header
              item-key="Id"
            >
              <template #[`item.source_type_id`]="{item}">
                <div v-if="getOrderSourceTypeById(item.SourceTypeId)">
                  <v-tooltip
                    color="indigo lighten-2"
                    right
                  >
                    <template #activator="{on}">
                      <v-avatar
                        size="28"
                        v-on="on"
                      >
                        <v-icon
                          v-if="item.SourceTypeId < 100"
                          color="indigo lighten-2"
                        >
                          {{ getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo }}
                        </v-icon>

                        <img
                          v-else
                          :src="getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo"
                        >
                      </v-avatar>
                    </template>

                    <span v-if="item.SourceTypeId < 100">
                      {{ getOrderSourceTypeLangById(item.SourceTypeId) && getOrderSourceTypeLangById(item.SourceTypeId).Desc }}
                    </span>
                    <span v-else>
                      {{ getOrderSourceTypeById(item.SourceTypeId).desc }}
                    </span>
                  </v-tooltip>
                </div>
              </template>

              <template #[`item.order_id`]="{item}">
                {{ item.OrderCode }}
              </template>

              <template #[`item.receipt_id`]="{item}">
                <template v-for="(docNum, idx) in item.DocumentNumber">
                  <div
                    v-if="docNum"
                    :key="`doc-name-${docNum}-${idx}`"
                  >
                    <v-chip
                      :color="getFiscalDocumentNumber(item, idx).DocColor"
                      class="mb-1 px-1 white--text"
                      label
                      x-small
                    >
                      {{ getFiscalDocumentNumber(item, idx).DocName }}
                    </v-chip>
                  </div>
                </template>
              </template>

              <template #[`item.receipt_type`]="{item}">
                <template v-for="(docNum, idx) in item.DocumentNumber">
                  <div
                    v-if="getFiscalDocumentNumber(item, idx).DocType"
                    :key="`doc-type-${docNum}-${idx}`"
                  >
                    <v-chip
                      :color="getFiscalDocumentNumber(item, idx).DocColor"
                      class="mb-1 px-1 white--text"
                      label
                      x-small
                    >
                      {{ getFiscalDocumentNumber(item, idx).DocType }}
                    </v-chip>
                  </div>
                </template>
              </template>

              <template #[`item.order_total`]="{item}">
                <template v-if="item.Total">
                  {{ item.Total | currency }}
                </template>
              </template>

              <template
                v-for="tax in taxes"
                #[`item.${tax.IdName}`]="{item}"
              >
                <template v-if="item[tax.IdName]">
                  {{ item[tax.IdName] | currency }}
                </template>

                <template v-if="item[tax.IdName+'_Vat']">
                  <span
                    :key="tax.IdName+'_Vat'"
                    class="grey--text"
                  >/ {{ item[tax.IdName + '_Vat'] | currency }}</span>
                </template>
              </template>

              <template #[`item.date_added`]="{item}">
                {{ item.Date }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <email-select-dialog
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </div>
</template>

<script>
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import FilterCommon from '@/mixins/filter/filterCommon.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import ToolbarPeriodSelector from '@/components/common/ToolbarPeriodSelector.vue'
import Translatable from '@/mixins/translatable.js'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FiscalDocTypeCodeEnum from '@/api/Enums/FiscalDocTypeCodeEnum.js'
import SourceType from '@/mixins/orders/sourceType.js'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'

export default {
  name      : 'SalesReportZ',
  components: {
    EmailSelectDialog,
    FilterOptions,
    ToolbarPeriodSelector,
    FilterSearch
  },
  directives: {},
  mixins    : [AppData, Auth, SourceType, FilterCommon, DataTable, Translatable],
  data() {
    return {
      selectDateRangeDialogVisible: false,
      emailSelectDialogVisible    : false,
      dataTable                   : {
        socketCommand: {
          list: window.SocketCommand.Reports.SalesReportZ.All
        },
        filter: {
          filter_date: 'today'
        },
        options: {
          defaultSortBy: 'order_id',
          sortBy       : ['order_id'],
          sortDesc     : [true]
        },
        headers: []
      },

      filtersModel: {
        lang           : 'Reports.SalesReportReceiptsZ',
        filterOrderType: {
          key  : 'filter_type',
          value: 0,
          items: this.$t('Reports.SalesReportReceiptsZ.Filter.filterOrderType.Items')
        },
        filterInvoiceType: {
          key  : 'filter_invoice_type',
          value: null,
          items: this.$t('Reports.SalesReportReceiptsZ.Filter.filterInvoiceType.Items')
        },
        filterSourceType: {
          key  : 'filter_source_type',
          value: null,
          items: this.$t('Reports.SalesReportReceiptsZ.Filter.filterInvoiceType.Items')
        }
      }
    }
  },
  computed: {
    taxes() {
      return this.appConfig?.TAX?.Taxes || []
    },

    tableHeaders() {
      const headers = []

      headers.push(
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.Source'),
          value    : 'source_type_id',
          align    : 'center',
          sortable : false,
          cellClass: 'text-caption'
        },
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.Order'),
          value    : 'order_id',
          align    : 'center',
          sortable : true,
          cellClass: 'text-caption'
        },
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.Receipt'),
          value    : 'receipt_id',
          align    : 'left',
          sortable : false,
          cellClass: 'text-caption'
        },
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.ReceiptType'),
          value    : 'receipt_type',
          align    : 'left',
          sortable : false,
          cellClass: 'text-caption'
        },
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.Total'),
          value    : 'order_total',
          align    : 'right',
          sortable : true,
          cellClass: 'text-caption'
        }
      )

      this.taxes.forEach(tax => {
        headers.push(
          {
            text     : this.getTranslatedField(tax, 'Name'),
            value    : `${tax.IdName}`,
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          }
        )
      })

      headers.push(
        {
          text     : this.$t('Reports.SalesReportReceiptsZ.Table.Headers.Date'),
          value    : 'date_added',
          align    : 'center',
          sortable : true,
          cellClass: 'text-caption'
        }
      )

      return headers
    }
  },
  watch: {},
  created() {
    this.dataTable.headers = this.tableHeaders
    this.filtersModel.filterSourceType.items = this.activeSourceTypes

    this.$bus.$on(window.SocketCommand.Reports.SalesReportZ.Export, this.onExportResult)
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Reports.SalesReportZ.Export, this.onExportResult)
  },
  methods: {
    getFiscalDocumentNumber(item, index) {
      const docSeries = item.DocumentSeries[index] || ''
      const docNumber = item.DocumentNumber[index] || ''
      const docType = item.InvoiceType[index] || ''
      const docTypeCode = item.InvoiceTypeCode[index] || ''

      return {
        DocName    : `${docSeries ? (docSeries + '-') : ''}${docNumber}`,
        DocSeries  : docSeries,
        DocNumber  : docNumber,
        DocType    : docType,
        DocTypeCode: docTypeCode,
        DocColor   : this.getFiscalDocumentColor(docTypeCode)
      }
    },

    getFiscalDocumentColor(docTypeCode) {
      if (FiscalDocTypeCodeEnum.GREEK_SALE_RECEIPT === docTypeCode) return 'green'
      if (FiscalDocTypeCodeEnum.GREEK_SERVICE_RECEIPT === docTypeCode) return 'green'
      if (FiscalDocTypeCodeEnum.GREEK_RECEIPT_CREDIT === docTypeCode) return 'red'

      if (FiscalDocTypeCodeEnum.GREEK_RECEIPT_SELF_SUPPLY === docTypeCode) return 'cyan darken-2'
      if (FiscalDocTypeCodeEnum.GREEK_RECEIPT_SELF_USE === docTypeCode) return 'cyan darken-2'

      if (FiscalDocTypeCodeEnum.GREEK_SALE_INVOICE === docTypeCode) return 'blue'
      if (FiscalDocTypeCodeEnum.GREEK_INVOICE_CREDIT_1 === docTypeCode) return 'red'
      if (FiscalDocTypeCodeEnum.GREEK_INVOICE_CREDIT_2 === docTypeCode) return 'red'

      if (FiscalDocTypeCodeEnum.GREEK_DOCUMENT_ORDER === docTypeCode) return 'blue-grey'

      return 'grey'
    },

    onExportButtonClick() {
      if (!this.dataTable?.filter?.filter_date) return
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails) {
      if (!Array.isArray(emails) || !emails?.length) return

      const dataTablePayload = this.dataTableRequestData()
      const payload = {
        Emails      : emails,
        Period      : this.dataTable.filter.filter_date,
        Type        : this.filtersModel?.filterOrderType?.value || null,
        InvoiceType : this.filtersModel?.filterInvoiceType?.value || null,
        SourceTypeId: this.filtersModel?.filterSourceType?.value || null,
        SortBy      : dataTablePayload.sort_by,
        OrderBy     : dataTablePayload.order_by
      }

      window.callAS(window.SocketCommand.Reports.SalesReportZ.Export, payload)
    },

    onExportResult(response) {
      if (response.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Reports.SalesReportReceiptsZ.Export.SuccessMessage'),
          type   : 'success',
          icon   : 'check',
          timeout: 5000
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    }
  }
}
</script>

<style scoped>

</style>
